import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useState } from 'react';
import { getMedialinksForNode, Medialink } from '../../../../api/medialink';
import useThrowAsyncError from '../../../../hooks/useThrowAsyncError';
import ModuleContext from '../../_contexts/ModuleContext';
import TableOfContentsContext from './TableOfContentsContext';

type MedialinksContextValue = {
  medialinks: Medialink[];
  setMedialinks: React.Dispatch<React.SetStateAction<Medialink[]>>;
  fetchMedialinks: () => Promise<void>;
  isLoading: boolean;
};

const MedialinksContext = createContext<MedialinksContextValue>({
  medialinks: [],
  setMedialinks: () => undefined,
  fetchMedialinks: () => Promise.resolve(),
  isLoading: true,
});

export default MedialinksContext;

export function MedialinksContextProvider({
  children,
  onlyExercises = false,
}: PropsWithChildren<{ onlyExercises?: boolean }>) {
  const handleError = useThrowAsyncError();

  const { selectedModule } = useContext(ModuleContext);
  const { selectedNode } = useContext(TableOfContentsContext);

  const [medialinks, setMedialinks] = useState<Medialink[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchMedialinks = useCallback(async () => {
    if (!selectedModule || !selectedNode) return;

    setIsLoading(true);
    try {
      const { data } = await getMedialinksForNode(selectedModule.id, selectedNode.id, true, onlyExercises);

      setMedialinks(data);
    } catch (e) {
      handleError(e);
    } finally {
      setIsLoading(false);
    }
  }, [handleError, onlyExercises, selectedModule, selectedNode]);

  useEffect(() => {
    fetchMedialinks();
  }, [fetchMedialinks]);

  return (
    <MedialinksContext.Provider value={{ medialinks, setMedialinks, fetchMedialinks, isLoading }}>
      {children}
    </MedialinksContext.Provider>
  );
}
